import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
// Must be the first local import: initializes the default Firebase app as a
// side effect before any other module (several of which call
// getAuth()/getFirestore()/getFunctions() at module top level) gets
// evaluated. See firebaseConfig.js for the full explanation.
import './firebaseConfig';
import ProtectedRoute from './components/ProtectedRoute';
import ReturnPage from './pages/ReturnPage';
import RefreshPage from './pages/RefreshPage';
import OnboardingPage from './pages/OnboardingPage';
import PlansPage from './pages/PlansPage';
import EmailVerifyPage from './pages/EmailVerifyPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import VerifyCodePage from './pages/VerifyCodePage';
import AdminLoginPage from './admin/AdminLoginPage';
import AdminMfaPage from './admin/AdminMfaPage';
import AdminDashboardPage from './admin/AdminDashboardPage';
import AdminForgotPasswordPage from './admin/AdminForgotPasswordPage';
import AdminSignupPage from './admin/AdminSignupPage';
import AdminSignupOtpPage from './admin/AdminSignupOtpPage';
import AdminAccountPage from './admin/AdminAccountPage';
import LocationsPage from './admin/LocationsPage';
import LocationMaintenancePage from './admin/LocationMaintenancePage';
import FloorPlanPage from './admin/FloorPlanPage';
import WorkzonesPage from './admin/WorkzonesPage';
import KDSStationsPage from './admin/KDSStationsPage';
import ReservationsPage from './admin/ReservationsPage';
import KDSStationView from './admin/KDSStationView';
import CategoriesPage from './admin/CategoriesPage';
import ProductsPage from './admin/ProductsPage';
import IngredientsPage from './admin/IngredientsPage';
import ExpensesPage from './admin/ExpensesPage';
import SuppliersPage from './admin/SuppliersPage';
import InventoryMovementsPage from './admin/InventoryMovementsPage';
import ProductOptionsPage from './admin/ProductOptionsPage';
import CustomersPage from './admin/CustomersPage';
import TeamManagementPage from './admin/TeamManagementPage';
import SalesPage from './admin/SalesPage';
import SalesReports from './admin/SalesReports';
import PosMaintenancePage from './admin/PosMaintenancePage';
import PosPage from './admin/PosPage';
import PosSessionsPage from './admin/PosSessionsPage';
import SettingsPage from './admin/SettingsPage';
import TwoFactorAuthPage from './admin/TwoFactorAuthPage';
import VkiosksPage from './admin/VkiosksPage';
import KioskPage from './admin/KioskPage';
import KioskCheckoutPage from './admin/KioskCheckoutPage';
import OnlineOrderingPage from './admin/OnlineOrderingPage';
import FodyFlightPage from './admin/FodyFlightPage';
import './App.css';

function App() {
  return (
    <Router>
      <Routes>
        {/* Public Routes — no auth needed */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin/signup" element={<AdminSignupPage />} />
        <Route path="/admin/signup/otp" element={<AdminSignupOtpPage />} />
        <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
        <Route path="/admin/mfa-verify" element={<AdminMfaPage />} />
        <Route path="/admin/logout" element={<Navigate to="/admin/login" />} />

        {/* Onboarding & Plans — accessible without full setup
            These pages have their own internal redirect logic */}
        <Route path="/admin/onboarding" element={<OnboardingPage />} />
        <Route path="/admin/plans" element={<PlansPage />} />

        {/* Public callback pages */}
        <Route path="/stripe/return" element={<ReturnPage />} />
        <Route path="/stripe/refresh" element={<RefreshPage />} />
        <Route path="/stripe-web" element={<SubscriptionSuccessPage />} />
        <Route path="/email-verify" element={<EmailVerifyPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-code" element={<VerifyCodePage />} />

        {/* Protected Routes — require completed Stripe onboarding + active plan.
            If anything is missing, ProtectedRoute redirects to /admin/onboarding */}
        <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
        <Route path="/admin/account" element={<ProtectedRoute><AdminAccountPage /></ProtectedRoute>} />
        <Route path="/admin/locations" element={<ProtectedRoute><LocationsPage /></ProtectedRoute>} />
        <Route path="/admin/locations/new" element={<ProtectedRoute><LocationMaintenancePage /></ProtectedRoute>} />
        <Route path="/admin/locations/edit/:id" element={<ProtectedRoute><LocationMaintenancePage /></ProtectedRoute>} />
        <Route path="/admin/floor-plan" element={<ProtectedRoute><FloorPlanPage /></ProtectedRoute>} />
        <Route path="/admin/floor-plan/:locationId" element={<ProtectedRoute><FloorPlanPage /></ProtectedRoute>} />
        <Route path="/admin/workzones" element={<ProtectedRoute><WorkzonesPage /></ProtectedRoute>} />
        <Route path="/admin/workzones/:locationId" element={<ProtectedRoute><WorkzonesPage /></ProtectedRoute>} />
        <Route path="/admin/kds-stations" element={<ProtectedRoute><KDSStationsPage /></ProtectedRoute>} />
        <Route path="/admin/kds-stations/:locationId" element={<ProtectedRoute><KDSStationsPage /></ProtectedRoute>} />
        <Route path="/admin/reservations" element={<ProtectedRoute><ReservationsPage /></ProtectedRoute>} />
        <Route path="/admin/kds-view/:stationId" element={<ProtectedRoute><KDSStationView /></ProtectedRoute>} />
        <Route path="/admin/categories" element={<ProtectedRoute><CategoriesPage /></ProtectedRoute>} />
        <Route path="/admin/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
        <Route path="/admin/ingredients" element={<ProtectedRoute><IngredientsPage /></ProtectedRoute>} />
        <Route path="/admin/expenses" element={<ProtectedRoute><ExpensesPage /></ProtectedRoute>} />
        <Route path="/admin/suppliers" element={<ProtectedRoute><SuppliersPage /></ProtectedRoute>} />
        <Route path="/admin/inventory-movements" element={<ProtectedRoute><InventoryMovementsPage /></ProtectedRoute>} />
        <Route path="/admin/product-options" element={<ProtectedRoute><ProductOptionsPage /></ProtectedRoute>} />
        <Route path="/admin/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
        <Route path="/admin/team/*" element={<ProtectedRoute><TeamManagementPage /></ProtectedRoute>} />
        <Route path="/admin/pay-periods" element={<ProtectedRoute><TeamManagementPage defaultTab="payperiods" /></ProtectedRoute>} />
        <Route path="/admin/shifts" element={<ProtectedRoute><TeamManagementPage defaultTab="shifts" /></ProtectedRoute>} />
        <Route path="/admin/schedule" element={<ProtectedRoute><TeamManagementPage defaultTab="schedule" /></ProtectedRoute>} />
        <Route path="/admin/rules" element={<ProtectedRoute><TeamManagementPage defaultTab="rules" /></ProtectedRoute>} />
        <Route path="/admin/timesheets" element={<ProtectedRoute><TeamManagementPage defaultTab="timesheets" /></ProtectedRoute>} />
        <Route path="/admin/sales" element={<ProtectedRoute><SalesPage /></ProtectedRoute>} />
        <Route path="/admin/sales-reports" element={<ProtectedRoute><SalesReports /></ProtectedRoute>} />
        <Route path="/admin/pos" element={<ProtectedRoute><PosMaintenancePage /></ProtectedRoute>} />
        <Route path="/admin/pos/:posId" element={<ProtectedRoute><PosPage /></ProtectedRoute>} />
        <Route path="/admin/pos-sessions" element={<ProtectedRoute><PosSessionsPage /></ProtectedRoute>} />
        <Route path="/admin/pos-sessions/:posId" element={<ProtectedRoute><PosSessionsPage /></ProtectedRoute>} />
        <Route path="/admin/vkiosks" element={<ProtectedRoute><VkiosksPage /></ProtectedRoute>} />
        <Route path="/admin/kiosk/:kioskId" element={<ProtectedRoute><KioskPage /></ProtectedRoute>} />
        <Route path="/admin/kiosk-checkout/:kioskId" element={<ProtectedRoute><KioskCheckoutPage /></ProtectedRoute>} />
        <Route path="/admin/online-ordering" element={<ProtectedRoute><OnlineOrderingPage /></ProtectedRoute>} />
        <Route path="/admin/fody-flight" element={<ProtectedRoute><FodyFlightPage /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/admin/two-factor" element={<ProtectedRoute><TwoFactorAuthPage /></ProtectedRoute>} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/admin/login" />} />
      </Routes>
    </Router>
  );
}

export default App;
